import { PassengerField, PassengerTypeKey } from 'Models/Enums';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { BookingWizardData, fareFieldLeg, getPassengerTickets } from '../../BookingWizardData/BaseBookingWizardData';

/** One traveller on the booking, as the fare table needs to name them. */
export interface MixedTraveller {
	/** `Adult 1`, `Child 2` — the type's own word, numbered within that type. */
	label: string;
	/** Their passenger type, for counting a group that has grown too big to name. */
	typeKey: PassengerTypeKey;
	/** The fare type they hold on the leg asked about, or undefined where they have none. */
	passengerClassId?: string;
	/** Its name as the booking was loaded with, for a fare type no sailing in view prices. */
	className?: string;
}

/**
 * Every fare type priced by any sailing in view, the given columns first and in their order.
 *
 * <p>Names for the mix are looked up here rather than in one sailing's list: a sailing on a vessel
 * without one of the booking's fare types does not price it, and the heading then called those
 * passengers `No fare type` although they are booked in it.</p>
 */
export function windowClasses(
	columns: TripClassPrice[],
	classPricesByFerryTrip?: { [tripId: string]: TripClassPrice[] },
): TripClassPrice[] {
	const byId = new Map(columns.map(classPrice => [classPrice.passengerClassId, classPrice]));
	Object.values(classPricesByFerryTrip ?? {})
		.flat()
		.forEach(classPrice => {
			if (!byId.has(classPrice.passengerClassId)) {
				byId.set(classPrice.passengerClassId, classPrice);
			}
		});
	return [...byId.values()];
}

/**
 * Every traveller, named and numbered, with the fare type they are in.
 *
 * <p>Named rather than counted because a booker looking at "your travellers are in different fare
 * types" is asking <b>which</b> — and the seating step, where they would go to change it, lists them
 * exactly this way. The numbering is within the type, so a party of two adults and a child reads
 * Adult 1, Adult 2, Child 1 rather than 1, 2, 3.</p>
 */
export function mixedTravellers(
	wizardData: BookingWizardData,
	leg: 'departure' | 'return',
): MixedTraveller[] {
	const travellers: MixedTraveller[] = [];
	const fareLeg = fareFieldLeg(wizardData, leg);

	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field) ?? [];
		const name = passengerTypeStore.getSingularName(passengerType.passengerTypeKey, true);

		tickets.forEach((ticket, index) => {
			travellers.push({
				// Only numbered where there is more than one of the type: "Adult 1" beside "Child 1"
				// on a party of two says less than "Adult" and "Child" do.
				label: tickets.length > 1 ? `${name} ${index + 1}` : name,
				typeKey: passengerType.passengerTypeKey,
				passengerClassId: fareLeg === 'return'
					? ticket.returningPassengerClassId
					: ticket.passengerClassId,
				className: fareLeg === 'return' ? ticket.returningPassengerClassName : ticket.passengerClassName,
			});
		});
	});

	return travellers;
}

/**
 * The mix itself, in one line — `2 Comfort · 1 Premium`.
 *
 * <p>Ordered by the fare types' own order rather than by how many are in each, so the summary reads
 * left to right the way the columns beside it do. A fare type this sailing does not sell is named
 * anyway: it is on the booking, and leaving it out would make the line add up to fewer people than
 * the booking carries.</p>
 */
export function mixSummary(
	travellers: MixedTraveller[],
	classPrices: TripClassPrice[],
	nameFallback: string = 'No fare type',
): string {
	const counts = new Map<string | undefined, number>();
	travellers.forEach(traveller => {
		counts.set(traveller.passengerClassId, (counts.get(traveller.passengerClassId) ?? 0) + 1);
	});

	const ordered = [
		...classPrices
			.filter(classPrice => counts.has(classPrice.passengerClassId))
			.map(classPrice => `${counts.get(classPrice.passengerClassId)} ${classPrice.name}`),
		// Whatever the columns did not account for — a fare type this vessel does not carry, or a
		// passenger with none at all.
		...[...counts.entries()]
			.filter(([id]) => !classPrices.some(x => x.passengerClassId === id))
			.map(([id, count]) => {
				const name = id ? travellers.find(x => x.passengerClassId === id)?.className : undefined;
				return `${count} ${name ?? nameFallback}`;
			}),
	];

	return ordered.join(' · ');
}

/** One fare type on the booking, with who is in it. */
export interface MixGroup {
	passengerClassId?: string;
	/** The fare type's name, or a stand-in where this sailing does not carry it. */
	name: string;
	colour?: string | null;
	/** The travellers in it, named the way {@link mixedTravellers} names them. */
	who: string[];
	/** Their passenger types, which is what the summary counts. */
	typeKeys: PassengerTypeKey[];
	/**
	 * Who is in this fare type, counted by passenger type — `10 Adults · 3 Senior / teens`.
	 *
	 * <p>Counted rather than named, at every party size. Naming them reads better on a party of
	 * three and cannot survive one of thirteen, where it was eleven names wrapped over four lines
	 * against a single fare type. Switching between the two by size was worse than either: one row
	 * counted while the next named, so a number in the same position meant a quantity on one line
	 * and which traveller on the next. The seating step is where individual travellers are read and
	 * changed.</p>
	 */
	summary: string;
}

/** {@link MixGroup.summary} — `10 Adults · 3 Senior / teens`. */
function countsOf(typeKeys: PassengerTypeKey[]): string {
	const counts = new Map<PassengerTypeKey, number>();
	typeKeys.forEach(key => counts.set(key, (counts.get(key) ?? 0) + 1));

	return [...counts.entries()]
		.map(([key, count]) => `${count} ${count === 1
			? passengerTypeStore.getSingularName(key, true)
			: passengerTypeStore.getPluralName(key, true)}`)
		.join(' \u00b7 ');
}

/**
 * The booking's travellers grouped by the fare type they hold.
 *
 * <p>Grouped rather than listed one per line because that is the shape of the decision: a party of
 * four in two fare types is two facts, not four, and on a phone four rows of "Adult 2 · Comfort"
 * is most of the card. Ordered by the fare types' own order, so the groups read in the same
 * sequence as the columns and the seating step.</p>
 */
export function mixGroups(
	travellers: MixedTraveller[],
	classPrices: TripClassPrice[],
	nameFallback: string = 'No fare type',
): MixGroup[] {
	const groups = new Map<string | undefined, MixGroup>();

	travellers.forEach(traveller => {
		const existing = groups.get(traveller.passengerClassId);
		if (existing) {
			existing.who.push(traveller.label);
			existing.typeKeys.push(traveller.typeKey);
			return;
		}
		const classPrice = classPrices.find(x => x.passengerClassId === traveller.passengerClassId);
		groups.set(traveller.passengerClassId, {
			passengerClassId: traveller.passengerClassId,
			name: classPrice?.name ?? (traveller.passengerClassId ? traveller.className : undefined) ?? nameFallback,
			colour: classPrice?.colour,
			who: [traveller.label],
			typeKeys: [traveller.typeKey],
			// Filled in below, once every traveller has been seen.
			summary: '',
		});
	});

	groups.forEach(group => {
		group.summary = countsOf(group.typeKeys);
	});

	// The column order, then anything the columns did not account for — a fare type this vessel does
	// not carry keeps its travellers rather than dropping them off the list.
	const known = classPrices
		.map(classPrice => groups.get(classPrice.passengerClassId))
		.filter((group): group is MixGroup => !!group);
	const unknown = [...groups.values()].filter(group => !known.includes(group));

	return [...known, ...unknown];
}
